import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const CONTRACTS     = ['Omni365', 'VP', 'Other'];
const FREQUENCIES   = ['', 'Weekly', 'Monthly', 'Quarterly', 'Twice a year', 'Yearly', 'Custom'];
const INTERVENTIONS = ['', 'Onsite', 'Remote'];

const cellInput = { width: '100%', minWidth: 0, fontSize: 12, padding: '5px 7px' };
const errorText = { fontSize: 11, color: 'var(--critical)', marginTop: 3 };

/* Text / number cell: keeps its own draft so a failed save never loses what
   was typed. Saves on blur (or Enter) only when the value actually changed. */
function TextCell({ value, onSave, error, type = 'text', label, placeholder }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  return (
    <div>
      <input className="input" type={type} min={type === 'number' ? 0 : undefined}
        value={draft} placeholder={placeholder} aria-label={label} aria-invalid={!!error}
        style={cellInput}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { if (String(draft) !== String(value ?? '')) onSave(draft); }}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      {error && <div style={errorText}>{error}</div>}
    </div>
  );
}

function SelectCell({ value, options, onSave, error, label }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  return (
    <div>
      <select className="input" value={draft} aria-label={label} aria-invalid={!!error} style={cellInput}
        onChange={e => { setDraft(e.target.value); onSave(e.target.value); }}>
        {options.map(o => (typeof o === 'string'
          ? <option key={o} value={o}>{o || '—'}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
      {error && <div style={errorText}>{error}</div>}
    </div>
  );
}

/* Technician(s): tick team members; stored as "Name / Name" in the order ticked.
   The list opens inline (not as a floating popover) so the scrolling table
   never clips it. A saved name outside the team stays visible and untickable. */
function TechniciansCell({ value, team, onSave, error }) {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  const selected = draft.split('/').map(s => s.trim()).filter(Boolean);
  const choices  = [...team, ...selected.filter(n => !team.includes(n))];
  const toggle = (name) => {
    const next = (selected.includes(name) ? selected.filter(n => n !== name) : [...selected, name]).join(' / ');
    setDraft(next);
    onSave(next);
  };
  return (
    <div>
      <details>
        <summary className="input" style={{ ...cellInput, cursor: 'pointer' }} aria-label="Technicians">
          {selected.length ? selected.join(' / ') : <span style={{ color: 'var(--text-2)' }}>Pick technicians…</span>}
        </summary>
        <div style={{ padding: '6px 2px 2px' }}>
          {choices.map(name => (
            <label key={name} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '2px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.includes(name)} onChange={() => toggle(name)} />
              {name}
            </label>
          ))}
          {choices.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-2)' }}>No team members found</div>}
        </div>
      </details>
      {error && <div style={errorText}>{error}</div>}
    </div>
  );
}

/* Scheduled / Communicated: the effective value, where it came from, and a way
   back to the automatic value once it has been set by hand. */
function StatusCell({ checked, source, detail, label, onSet, error }) {
  return (
    <div style={{ minWidth: 110 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
        <input type="checkbox" checked={!!checked} aria-label={label}
          onChange={e => onSet(e.target.checked)} />
        <span className="mono" style={{ color: 'var(--text-2)' }}>{detail}</span>
      </label>
      {source === 'manual' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 11 }}>
          <span className="badge" title="Set by hand">manual</span>
          <button type="button" className="btn btn-ghost"
            style={{ fontSize: 11, padding: '1px 6px' }}
            onClick={() => onSet(null)} aria-label={`${label}: return to automatic`}>
            back to auto
          </button>
        </div>
      )}
      {error && <div style={errorText}>{error}</div>}
    </div>
  );
}

function rowBackground(r) {
  if (!r.plan_key) return 'var(--warn-light)';                       // nothing planned yet
  if (r.scheduled && !r.communicated) return 'color-mix(in srgb, var(--warn-light) 50%, transparent)';
  return undefined;
}

export default function MaintenanceTrackerPage({ jiraUrl }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear]         = useState(thisYear);
  const [rows, setRows]         = useState([]);
  const [projects, setProjects] = useState([]);
  const [plans, setPlans]       = useState([]);
  const [team, setTeam]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [cellErrors, setCellErrors] = useState({});
  const [newKey, setNewKey]     = useState('');
  const [adding, setAdding]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/tracker?year=${year}`)
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Could not load the tracker'); return d; })
      .then(d => { setRows(d); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API}/api/planning/projects`).then(r => (r.ok ? r.json() : [])).then(setProjects).catch(() => setProjects([]));
    fetch(`${API}/api/planning/history?limit=500`).then(r => (r.ok ? r.json() : [])).then(setPlans).catch(() => setPlans([]));
    fetch(`${API}/api/tracker/technicians`).then(r => (r.ok ? r.json() : [])).then(setTeam).catch(() => setTeam([]));
  }, []);

  const save = async (row, patch) => {
    const key = `${row.id}:${Object.keys(patch)[0]}`;
    try {
      const r = await fetch(`${API}/api/tracker/${row.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Not saved');
      setRows(rs => rs.map(x => (x.id === row.id ? d : x)));
      setCellErrors(ce => { const n = { ...ce }; delete n[key]; return n; });
    } catch (e) {
      setCellErrors(ce => ({ ...ce, [key]: e.message }));
    }
  };

  const addRow = async () => {
    const project = projects.find(p => p.key === newKey);
    if (!project) return;
    setAdding(true);
    try {
      const r = await fetch(`${API}/api/tracker`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, customer_key: project.key, customer_name: project.name }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not add the customer');
      setNewKey('');
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove ${row.customer_name} (${year}) from the tracker?`)) return;
    const r = await fetch(`${API}/api/tracker/${row.id}`, { method: 'DELETE' });
    if (r.ok) setRows(rs => rs.filter(x => x.id !== row.id));
    else setError('Could not remove the row');
  };

  const err = (row, field) => cellErrors[`${row.id}:${field}`];
  const years = [thisYear - 1, thisYear, thisYear + 1, thisYear + 2];
  const withoutPlan = rows.filter(r => !r.plan_key).length;
  const notTold     = rows.filter(r => r.plan_key && r.scheduled && !r.communicated).length;

  return (
    <div>
      <div className="card card-spaced-lg" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          Year
          <select className="input" value={year} onChange={e => setYear(Number(e.target.value))} style={{ width: 100 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
          {rows.length} customer{rows.length === 1 ? '' : 's'} · {withoutPlan} without a plan · {notTold} not yet communicated
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-2)', marginLeft: 'auto' }}>
          Scheduled and Communicated follow the linked plan unless set by hand.
        </span>
      </div>

      {error && <div className="card" style={{ color: 'var(--critical)', marginBottom: 12 }} role="alert">{error}</div>}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Customer</th><th>Contract</th><th>Per year</th><th>Frequency</th>
                <th>Occurrence</th><th>Onsite / Remote</th><th>Technician(s)</th>
                <th>Plan</th><th>Scheduled</th><th>Communicated</th><th>Notes</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={12} style={{ color: 'var(--text-2)' }}>Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={12} style={{ color: 'var(--text-2)' }}>No customers tracked for {year} yet — add one below.</td></tr>
              )}
              {rows.map(r => {
                const customerPlans = plans.filter(p => p.project_key === r.customer_key);
                const planOptions = [{ value: '', label: '— none —' },
                  ...customerPlans.map(p => ({ value: p.root_key, label: `${p.root_key} · ${p.first_date} → ${p.last_date}` }))];
                if (r.plan_key && !customerPlans.some(p => p.root_key === r.plan_key)) {
                  planOptions.push({ value: r.plan_key, label: r.plan_key });
                }
                return (
                  <tr key={r.id} style={{ background: rowBackground(r), verticalAlign: 'top' }}>
                    <td style={{ minWidth: 150 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.customer_name}</div>
                      <div className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{r.customer_key}</div>
                    </td>
                    <td style={{ minWidth: 95 }}>
                      <SelectCell value={r.contract} options={CONTRACTS} label="Contract"
                        error={err(r, 'contract')} onSave={v => save(r, { contract: v })} />
                    </td>
                    <td style={{ width: 70 }}>
                      <TextCell type="number" value={r.maintenances_per_year ?? ''} label="Maintenances per year"
                        error={err(r, 'maintenances_per_year')} onSave={v => save(r, { maintenances_per_year: v })} />
                    </td>
                    <td style={{ minWidth: 110 }}>
                      <SelectCell value={r.frequency} options={FREQUENCIES} label="Frequency"
                        error={err(r, 'frequency')} onSave={v => save(r, { frequency: v })} />
                    </td>
                    <td style={{ minWidth: 110 }}>
                      <TextCell value={r.occurrence} label="Occurrence" placeholder="e.g. 2nd Tuesday"
                        error={err(r, 'occurrence')} onSave={v => save(r, { occurrence: v })} />
                    </td>
                    <td style={{ minWidth: 95 }}>
                      <SelectCell value={r.intervention_type} options={INTERVENTIONS} label="Onsite or remote"
                        error={err(r, 'intervention_type')} onSave={v => save(r, { intervention_type: v })} />
                    </td>
                    <td style={{ minWidth: 160 }}>
                      <TechniciansCell value={r.technicians} team={team}
                        error={err(r, 'technicians')} onSave={v => save(r, { technicians: v })} />
                    </td>
                    <td style={{ minWidth: 170 }}>
                      <SelectCell value={r.plan_key} options={planOptions} label="Linked plan"
                        error={err(r, 'plan_key')} onSave={v => save(r, { plan_key: v })} />
                      {r.plan_key && jiraUrl && (
                        <a href={`${jiraUrl}/browse/${r.plan_key}`} target="_blank" rel="noreferrer"
                          className="mono" style={{ fontSize: 11 }}>Open {r.plan_key}</a>
                      )}
                    </td>
                    <td>
                      <StatusCell checked={r.scheduled} source={r.scheduled_source} label="Scheduled"
                        detail={r.plan_key || r.maintenances_per_year != null ? r.scheduled_label : ''}
                        error={err(r, 'scheduled_override')} onSet={v => save(r, { scheduled_override: v })} />
                    </td>
                    <td>
                      <StatusCell checked={r.communicated} source={r.communicated_source} label="Communicated"
                        detail={r.last_email_display}
                        error={err(r, 'communicated_override')} onSet={v => save(r, { communicated_override: v })} />
                    </td>
                    <td style={{ minWidth: 180 }}>
                      <TextCell value={r.notes} label="Notes"
                        error={err(r, 'notes')} onSave={v => save(r, { notes: v })} />
                    </td>
                    <td>
                      <button type="button" className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--critical)' }}
                        onClick={() => removeRow(r)} aria-label={`Remove ${r.customer_name}`}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
          <select className="input" value={newKey} onChange={e => setNewKey(e.target.value)}
            aria-label="Customer to add" style={{ minWidth: 260, maxWidth: '100%' }}>
            <option value="">Add a customer for {year}…</option>
            {projects.map(p => <option key={p.key} value={p.key}>{p.name} ({p.key})</option>)}
          </select>
          <button type="button" className="btn btn-primary" onClick={addRow} disabled={!newKey || adding}>
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
